import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppError, ErrorType, NhsErrorCode, BackendErrorCode } from '../models/error.model';
import { HttpService } from '../utils/http.service';
import { PathsService } from './state/paths.service';

/**
 * Error definition shape used by the static error library.
 * `titleKey` and `messageKey` are i18n keys that will be resolved through
 * TranslateService at the moment the error is set. They follow the pattern
 * `error.<errorCode>.title` / `error.<errorCode>.message`.
 */
interface ErrorDefinition {
  type: ErrorType;
  titleKey: string;
  messageKey: string;
  targetPage: string;
  dismissible: boolean;
}

/**
 * Error Handling Service
 *
 * Centralized service for managing application errors.
 * Provides clean error handling without exposing details in URLs.
 *
 * All static error definitions store translation keys, not literal text.
 * At setError time the keys are resolved via TranslateService so the user
 * sees the message in the active app language.
 *
 * Usage:
 * 1. Set error: errorService.setError('incorrect_password')
 * 2. Get error: errorService.getError().subscribe(error => { ... })
 * 3. Clear error: errorService.clearError()
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  private errorSubject = new BehaviorSubject<AppError | null>(null);
  public error$: Observable<AppError | null> = this.errorSubject.asObservable();

  // Error definitions library. titleKey/messageKey are resolved via
  // TranslateService when an error is set.
  private readonly errorDefinitions: Record<string, ErrorDefinition> = {
    // NHS Errors
    [NhsErrorCode.ACCOUNT_EXISTS]: {
      type: 'info',
      titleKey: 'error.nhs_account_account_exists.title',
      messageKey: 'error.nhs_account_account_exists.message',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.NAME_MISMATCH]: {
      type: 'error',
      titleKey: 'error.nhs_account_name_mismatch.title',
      messageKey: 'error.nhs_account_name_mismatch.message',
      targetPage: '/guest',
      dismissible: true
    },
    [NhsErrorCode.NOT_REGISTERED]: {
      type: 'warning',
      titleKey: 'error.nhs_account_not_registered.title',
      messageKey: 'error.nhs_account_not_registered.message',
      targetPage: '/guest',
      dismissible: true
    },
    [NhsErrorCode.CONSENT_NOT_GIVEN]: {
      type: 'error',
      titleKey: 'error.ConsentNotGiven.title',
      messageKey: 'error.ConsentNotGiven.message',
      targetPage: '/create-account',
      dismissible: true
    },
    [NhsErrorCode.AGE_RESTRICTION]: {
      type: 'error',
      titleKey: 'error.age18.title',
      messageKey: 'error.age18.message',
      targetPage: '/guest',
      dismissible: true
    },
    [NhsErrorCode.RECURRING_USER]: {
      type: 'info',
      titleKey: 'error.recurringNhsUser.title',
      messageKey: 'error.recurringNhsUser.message',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.LOGIN_REQUIRED]: {
      type: 'warning',
      titleKey: 'error.nhs_login_required.title',
      messageKey: 'error.nhs_login_required.message',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.CANNOT_LOGIN]: {
      type: 'error',
      titleKey: 'error.cannotlogin.title',
      messageKey: 'error.cannotlogin.message',
      targetPage: '/login',
      dismissible: true
    },
    [NhsErrorCode.NHS_ACCOUNT_LINKED]: {
      type: 'info',
      titleKey: 'error.nhs_account.title',
      messageKey: 'error.nhs_account.message',
      targetPage: '/login',
      dismissible: true
    },

    // Backend Errors
    [BackendErrorCode.EMAIL_NOT_FOUND]: {
      type: 'error',
      titleKey: 'error.email_not_found.title',
      messageKey: 'error.email_not_found.message',
      targetPage: '/login',
      dismissible: false
    },
    [BackendErrorCode.INCORRECT_PASSWORD]: {
      type: 'error',
      titleKey: 'error.incorrect_password.title',
      messageKey: 'error.incorrect_password.message',
      targetPage: '/login',
      dismissible: false
    },
    [BackendErrorCode.MFA_CODE_ERROR]: {
      type: 'error',
      titleKey: 'error.mfa_code_error.title',
      messageKey: 'error.mfa_code_error.message',
      targetPage: '/login',
      dismissible: true
    },
    [BackendErrorCode.FORGOT_SUCCESS]: {
      type: 'success',
      titleKey: 'error.forgot_success.title',
      messageKey: 'error.forgot_success.message',
      targetPage: '/login',
      dismissible: true
    }
  };

  constructor(
    private httpService: HttpService,
    private pathsService: PathsService,
    private translate: TranslateService
  ) {}

  /**
   * Resolve a translation key with a safe fallback.
   * If the key is not present in the active translation, the fallback is returned.
   */
  private resolveKey(key: string, fallback: string): string {
    const resolved = this.translate.instant(key);
    // translate.instant returns the key itself when missing; detect that.
    if (!resolved || resolved === key) {
      return fallback;
    }
    return resolved;
  }

  /**
   * Set an error using predefined error code
   * Automatically resolves target page for NHS errors based on user flow
   */
  async setError(errorCode: string, params?: Record<string, any>): Promise<void> {
    const definition = this.errorDefinitions[errorCode];

    if (definition) {
      // Get last visited URLs for NHS errors to determine correct target page
      let targetPage = definition.targetPage;
      
      // Check if this is an NHS flow error that needs dynamic routing
      const nhsFlowErrors = [
        NhsErrorCode.CONSENT_NOT_GIVEN,
        NhsErrorCode.CANNOT_LOGIN,
        NhsErrorCode.AGE_RESTRICTION
      ];

      if (nhsFlowErrors.includes(errorCode as NhsErrorCode)) {
        try {
          const lastUrl = localStorage.getItem('lastVisitedUrl') || undefined;

          targetPage = this.getNhsErrorTargetPage(errorCode, lastUrl);
          console.log('[ErrorService] Resolved NHS error target page:', targetPage, 'for error:', errorCode);
        } catch (error) {
          console.warn('[ErrorService] Could not get last visited URLs, using default target page:', error);
        }
      }

      const appError: AppError = {
        code: errorCode,
        type: definition.type,
        title: this.resolveKey(definition.titleKey, errorCode),
        message: this.resolveKey(definition.messageKey, ''),
        targetPage: targetPage,
        dismissible: definition.dismissible,
        params,
        timestamp: Date.now()
      };
      this.errorSubject.next(appError);
    } else {
      // Fallback for undefined errors
      console.warn(`Error code '${errorCode}' not found in definitions`);
      this.httpService.getError(errorCode).then(apiError => {

        try {
          if (apiError) {
            // Ensure we set basic fields required by AppError
            const error: AppError = {
              code: errorCode,
              type: apiError.type || 'error',
              title: this.resolveKey(
                `error.${errorCode}.title`,
                apiError.title || 'An error occurred'
              ),
              message: this.resolveKey(
                `error.${errorCode}.message`,
                apiError.message || ''
              ),
              targetPage: apiError.targetPage || '/guest',
              dismissible: apiError.dismissible !== undefined ? apiError.dismissible : true,
              params: apiError.params || params,
              timestamp: Date.now()
            };
            this.errorSubject.next(error);
            return;
          }
        } catch (err) {
          console.warn('Failed to fetch error from API:', err);
        }


        this.setCustomError({
          code: errorCode,
          type: 'error',
          title: this.resolveKey('error.generic.title', 'An error occurred'),
          message: this.resolveKey('error.generic.message', 'Please try again later.'),
          targetPage: '/guest',
          params
        });

      });
    }
  }

  /**
   * Set a custom error with full control
   */
  setCustomError(error: Omit<AppError, 'timestamp'>): void {
    this.errorSubject.next({
      ...error,
      timestamp: Date.now()
    });
  }

  /**
   * Get current error as observable
   */
  getError(): Observable<AppError | null> {
    return this.error$;
  }

  /**
   * Get current error value (synchronous)
   */
  getCurrentError(): AppError | null {
    return this.errorSubject.value;
  }

  /**
   * Check if there is an active error
   */
  hasError(): boolean {
    return this.errorSubject.value !== null;
  }

  /**
   * Check if error is for specific page
   */
  hasErrorForPage(pagePath: string): boolean {
    const error = this.errorSubject.value;
    return error !== null && error.targetPage === pagePath;
  }

  /**
   * Clear current error
   */
  clearError(): void {
    this.errorSubject.next(null);
  }

  /**
   * Get error definition by code (for preview/testing).
   * Resolves the translation keys to literal title/message strings so
   * callers receive the same shape as before.
   */
  getErrorDefinition(errorCode: string): Omit<AppError, 'code' | 'timestamp'> | undefined {
    const def = this.errorDefinitions[errorCode];
    if (!def) {
      return undefined;
    }
    return {
      type: def.type,
      title: this.resolveKey(def.titleKey, errorCode),
      message: this.resolveKey(def.messageKey, ''),
      targetPage: def.targetPage,
      dismissible: def.dismissible
    };
  }

  /**
   * Get all available error codes
   */
  getAvailableErrorCodes(): string[] {
    return Object.keys(this.errorDefinitions);
  }

  /**
   * Get appropriate target page for NHS errors based on user flow
   * Uses lastVisitedUrl to determine if user was on login or registration
   * @param errorCode - The NHS error code
   * @param lastVisitedUrl - The last page the user was on (from PathsService)
   * @param secondLastVisitedUrl - The second-to-last page the user was on
   * @returns The appropriate target page path
   */
  getNhsErrorTargetPage(errorCode: string, lastVisitedUrl?: string): string {
    // Check if this is an NHS error that can appear on both login and registration
    const nhsFlowErrors = [
      NhsErrorCode.CONSENT_NOT_GIVEN,
      NhsErrorCode.CANNOT_LOGIN,
      NhsErrorCode.AGE_RESTRICTION
    ];
    const errorDef = this.errorDefinitions[errorCode];
    console.log('[ErrorService] Target page resolution for NHS error:', errorDef.targetPage);

    // If it's a flow error, determine target based on lastVisitedUrl
    if (nhsFlowErrors.includes(errorCode as NhsErrorCode)) {
      console.log('[ErrorService] Checking NHS error target page for:', errorCode, 'lastVisitedUrl:', lastVisitedUrl);
      
      return errorDef?.targetPage || '/login';
    } 
    else {
      // Non-flow NHS errors use their predefined target page
      console.log('[ErrorService] Non-flow NHS error, using default target page:', errorDef?.targetPage || '/guest');
      return errorDef?.targetPage || '/guest';
    }
  }
}
